import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Shop.css";

import FadeImg from "../components/FadeImg";
import { useNavDirection } from "../NavDirectionContext";

// Images produit (8 images)
import productImg1 from "../assets/Bonnet.jpg";
import productImg2 from "../assets/Bonnet_2.jpg";
import productImg3 from "../assets/Bonnet_3.jpg";
import productImg4 from "../assets/Bonnet_4.jpg";
import productImg5 from "../assets/Bonnet_5.jpg";
import productImg6 from "../assets/Bonnet_6.jpg";
import productImg7 from "../assets/Bonnet_7.jpg";
import productImg8 from "../assets/Bonnet_8.jpg";

export default function Shop() {
  const navigate = useNavigate();
  const { setDir } = useNavDirection();

  const images = useMemo(
    () =>
      [
        productImg1,
        productImg2,
        productImg3,
        productImg4,
        productImg5,
        productImg6,
        productImg7,
        productImg8,
      ].filter(Boolean),
    []
  );

  const [active, setActive] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (active > images.length - 1) setActive(0);
  }, [active, images.length]);

  // ✅ Observe le carousel (mobile) pour savoir quel slide est visible -> dots
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const slides = Array.from(el.querySelectorAll(".shop2__slide"));
    if (!slides.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
        if (!visible) return;
        const idx = slides.indexOf(visible.target);
        if (idx >= 0) setMobileIndex(idx);
      },
      { root: el, threshold: [0.5, 0.6, 0.7] }
    );

    slides.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [images.length]);

  const scrollToIndex = (idx) => {
    const el = carouselRef.current;
    if (!el) return;
    const slides = Array.from(el.querySelectorAll(".shop2__slide"));
    const target = slides[idx];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  const prevSlide = () => scrollToIndex(Math.max(0, mobileIndex - 1));
  const nextSlide = () => scrollToIndex(Math.min(images.length - 1, mobileIndex + 1));

  // Desktop: navigation image principale (avec retour au début/fin)
  const prevImg = () =>
    setActive((a) => (a - 1 + images.length) % images.length);
  const nextImg = () => setActive((a) => (a + 1) % images.length);

  const goBack = () => {
    setDir("down");
    navigate("/shop");
  };

  const goSubscribe = () => {
    setDir("up");
    navigate("/subscribe");
  };

  const SHOP_DOMAIN = "checkout.ghettosymphony.store";
  const VARIANT_ID = "52718608056658";

  const buyNow = () => {
    window.location.href = `https://${SHOP_DOMAIN}/cart/add?id=${VARIANT_ID}&quantity=1&return_to=/cart`;
  };

  return (
    <div className="shop2">
      <button className="shop2__back" onClick={goBack}>
        back
      </button>

      <div className="shop2__centerWrap">
        <main className="shop2__main">
          <section className="shop2__left">
            {/* ✅ Mobile carousel + indicators */}
            <div className="shop2__carouselWrap">
              <div
                ref={carouselRef}
                className="shop2__carousel"
                aria-label="Product images"
              >
                {images.map((src, i) => (
                  <div className="shop2__slide" key={`${i}-${src}`}>
                    <FadeImg
                      className="shop2__slideImg"
                      src={src}
                      alt={`Product image ${i + 1}`}
                      draggable="false"
                    />
                  </div>
                ))}
              </div>

              {/* Flèches (mobile only via CSS) */}
              <button
                type="button"
                className="shop2__arrow shop2__arrow--left"
                onClick={prevSlide}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                type="button"
                className="shop2__arrow shop2__arrow--right"
                onClick={nextSlide}
                aria-label="Next image"
              >
                ›
              </button>

              {/* Dots (mobile only via CSS) */}
              <div className="shop2__dots" aria-label="Image position">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`shop2__dotInd ${i === mobileIndex ? "isActive" : ""}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>

            {/* Desktop: image principale */}
            <div className="shop2__imgWrap">
              <button
                type="button"
                className="shop2__imgArrow shop2__imgArrow--left"
                onClick={prevImg}
                aria-label="Previous image"
              >
                ‹
              </button>

              <FadeImg
                className="shop2__img"
                src={images[active]}
                alt="Product"
                draggable="false"
              />

              <button
                type="button"
                className="shop2__imgArrow shop2__imgArrow--right"
                onClick={nextImg}
                aria-label="Next image"
              >
                ›
              </button>
            </div>
          </section>

          <section className="shop2__right">
            <div className="shop2__titleRow">
              <h1 className="shop2__title">imani beanie with satin</h1>

              <div className="shop2__swatches" aria-label="colors">
                <span className="shop2__dot shop2__dot--burgundy" />
                <span className="shop2__dot shop2__dot--navy" />
              </div>
            </div>

            <div className="shop2__text">
              <p>
                Imani est le premier enfant de Guetto Symphony.
                <br />
                Son nom signifie "foi" en swahili — une foi en soi, en ses racines
                et en sa vision.
              </p>

              <p>
                Il incarne un équilibre entre l'esthétique core Y2K underground,
                l'identité Afro, et une dimension spirituelle manifestée.
              </p>

              <p>Il se porte comme un symbole profond et de style.</p>
            </div>

            {/* Desktop: thumbs sous le texte */}
            <div className="shop2__thumbs" aria-label="Thumbnails">
              {images.map((src, i) => (
                <button
                  key={`${i}-${src}`}
                  className={`shop2__thumb ${i === active ? "isActive" : ""}`}
                  onClick={() => setActive(i)}
                  aria-label={`View image ${i + 1}`}
                  type="button"
                >
                  <FadeImg src={src} alt="" draggable="false" />
                </button>
              ))}
            </div>

            <div className="shop2__price">39,99€</div>

            <div className="shopShipping">
              <div className="shopShippingTitle">shipping</div>
              <div className="shopShippingText">
                this item is available now. shipping calculated at checkout.
              </div>
            </div>

            <div className="shop2__actions">
              <button className="shop2__btn shop2__btn--black" onClick={buyNow}>
                order
              </button>

              <button className="shop2__btn shop2__btn--ghost" onClick={goSubscribe}>
                notify me
              </button>
            </div>

            {/* Mobile spacer (CSS shows it only on phone) */}
            <div className="shop2__bottomSpacer" aria-hidden="true" />
          </section>
        </main>
      </div>
    </div>
  );
}